import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import dayjs from 'dayjs';
import { getHeaders } from '../../utility/getHeader';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tag,
    Space,
    Typography,
    Button,
    DatePicker,
    Select,
} from 'antd';
import {
    LineChartOutlined,
    DownloadOutlined,
    FilterOutlined,
    BankOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    FilePdfOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text } = Typography;
const { Option } = Select;

export default function BankReports() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({});
    const [companyProfile, setCompanyProfile] = useState({});
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        bank_account_id: '',
        start_date: '',
        end_date: '',
        transaction_type: '',
        transaction_category: ''
    });

    const transactionTypes = ['Credit', 'Debit'];
    const transactionCategories = ['Sales', 'Purchase', 'Payment_Received', 'Payment_Made', 'Transfer', 'Adjustment', 'Opening_Balance'];

    useEffect(() => {
        fetchBankAccounts();
        fetchTransactions();
        fetchSummary();
        fetchCompanyProfile();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchBankAccounts = async () => {
        try {
            const response = await axios.get('/bank-accounts', getHeaders());
            setBankAccounts(response.data.data || []);
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams(filters);
            const response = await axios.get(
                `/bank-transactions?${queryParams}`,
                getHeaders()
            );
            setTransactions(response.data.data || []);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions');
        }
        setLoading(false);
    };

    const fetchSummary = async () => {
        try {
            const response = await axios.get(
                '/bank-summary',
                getHeaders()
            );
            setSummary(response.data.data || {});
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const fetchCompanyProfile = async () => {
        try {
            const response = await axios.get('/company-profile', getHeaders());
            if (response.data.success && response.data.data.length > 0) {
                setCompanyProfile(response.data.data[0]);
            }
        } catch (error) {
            console.error('Error fetching company profile:', error);
        }
    };

    const applyFilters = () => {
        fetchTransactions();
    };

    const resetFilters = () => {
        setFilters({
            bank_account_id: '',
            start_date: '',
            end_date: '',
            transaction_type: '',
            transaction_category: ''
        });
        setTimeout(() => fetchTransactions(), 100);
    };

    const exportToCSV = () => {
        if (transactions.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const headers = ['Date', 'Account', 'Type', 'Category', 'Description', 'Amount', 'Balance After'];
        const csvData = transactions.map(txn => [
            new Date(txn.transaction_date).toLocaleDateString(),
            txn.account_name,
            txn.transaction_type,
            txn.transaction_category,
            txn.description || '',
            txn.amount,
            txn.balance_after
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `bank_transactions_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToPDF = () => {
        if (transactions.length === 0) {
            toast.warning('No data to export');
            return;
        }

        const doc = new jsPDF();

        let currentY = 15;

        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        if (companyProfile.name) {
            doc.text(companyProfile.name, 14, currentY);
            currentY += 8;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        if (companyProfile.address) {
            doc.text(`Address: ${companyProfile.address}`, 14, currentY);
            currentY += 5;
        }
        if (companyProfile.phone_number) {
            doc.text(`Phone: ${companyProfile.phone_number}`, 14, currentY);
            currentY += 5;
        }
        if (companyProfile.email) {
            doc.text(`Email: ${companyProfile.email}`, 14, currentY);
            currentY += 8;
        }

        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('Bank Transactions Report', 14, currentY);
        currentY += 6;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })}`, 14, currentY);
        currentY += 8;

        const tableColumn = [
            'Date',
            'Account',
            'Type',
            'Category',
            'Description',
            'Amount',
            'Balance After'
        ];
        const tableRows = [];

        let totalCredits = 0;
        let totalDebits = 0;

        transactions.forEach(txn => {
            const amount = parseFloat(txn.amount) || 0;

            if (txn.transaction_type === 'Credit') {
                totalCredits += amount;
            } else {
                totalDebits += amount;
            }

            tableRows.push([
                formatDate(txn.transaction_date),
                (txn.account_name || 'N/A').substring(0, 15),
                txn.transaction_type,
                (txn.transaction_category || 'N/A').substring(0, 12),
                (txn.description || '').substring(0, 25),
                `${txn.transaction_type === 'Credit' ? '+' : '-'}${formatCurrency(amount)}`,
                formatCurrency(txn.balance_after)
            ]);
        });

        tableRows.push([
            { content: '', colSpan: 4, styles: { fillColor: [240, 240, 240] } },
            { content: 'Summary', styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', colSpan: 2, styles: { fillColor: [240, 240, 240] } }
        ]);

        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Total Credits:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `+${formatCurrency(totalCredits)}`, styles: { fontStyle: 'bold', textColor: [76, 175, 80] } },
            { content: '', styles: {} }
        ]);

        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Total Debits:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `-${formatCurrency(totalDebits)}`, styles: { fontStyle: 'bold', textColor: [244, 67, 54] } },
            { content: '', styles: {} }
        ]);

        tableRows.push([
            { content: '', colSpan: 4 },
            { content: 'Net Balance:', styles: { halign: 'right', fontStyle: 'bold' } },
            { content: formatCurrency(summary.total_balance || 0), styles: { fontStyle: 'bold', textColor: [13, 148, 136] } },
            { content: '', styles: {} }
        ]);

        doc.autoTable({
            startY: currentY,
            head: [tableColumn],
            body: tableRows,
            styles: {
                fontSize: 8,
                cellPadding: 2
            },
            headStyles: {
                fillColor: [13, 148, 136],
                textColor: [255, 255, 255],
                fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: 20 },
                1: { cellWidth: 25 },
                2: { cellWidth: 15 },
                3: { cellWidth: 20 },
                4: { cellWidth: 35 },
                5: { cellWidth: 25, halign: 'right' },
                6: { cellWidth: 25, halign: 'right' }
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            }
        });

        const filename = `Bank_Transactions_${companyProfile.name ? companyProfile.name.replace(/\s+/g, '_') : 'StoreHub'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);

        toast.success('PDF report downloaded successfully!');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount) + ' THB';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const transactionColumns = [
        { title: 'Date', dataIndex: 'transaction_date', key: 'date', render: (v) => formatDate(v) },
        {
            title: 'Account',
            key: 'account',
            render: (_, t) => (
                <div>
                    <div><Text strong>{t.account_name}</Text></div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t.bank_name}</Text>
                </div>
            )
        },
        {
            title: 'Type',
            dataIndex: 'transaction_type',
            key: 'type',
            render: (v) => (
                <Tag color={v === 'Credit' ? 'green' : 'red'} icon={v === 'Credit' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
                    {v}
                </Tag>
            )
        },
        {
            title: 'Category',
            dataIndex: 'transaction_category',
            key: 'category',
            render: (v) => <Tag color="cyan">{(v || '').replace('_', ' ')}</Tag>
        },
        { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: 'Reference',
            dataIndex: 'reference_number',
            key: 'reference_number',
            render: (v) => v ? <Tag color="blue">{v}</Tag> : '-'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (v, t) => (
                <Text strong style={{ color: t.transaction_type === 'Credit' ? '#16a34a' : '#dc2626' }}>
                    {t.transaction_type === 'Credit' ? '+' : '-'}{formatCurrency(v)}
                </Text>
            )
        },
        { title: 'Balance After', dataIndex: 'balance_after', key: 'balance_after', align: 'right', render: (v) => <Text strong>{formatCurrency(v)}</Text> },
    ];

    return (
        <Layout>
            <Header title="Bank Reports & Transactions" />
            <ToastContainer />

            <div style={{ padding: 20 }}>
                {/* Summary Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdfa' }}>
                            <Statistic title="Total Balance" value={summary.total_balance || 0} precision={2} prefix={<BankOutlined />} suffix="THB" valueStyle={{ color: '#0d9488' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#f0fdf4' }}>
                            <Statistic title="Total Credits" value={summary.total_credits || 0} precision={2} prefix={<ArrowUpOutlined />} suffix="THB" valueStyle={{ color: '#16a34a' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#fef2f2' }}>
                            <Statistic title="Total Debits" value={summary.total_debits || 0} precision={2} prefix={<ArrowDownOutlined />} suffix="THB" valueStyle={{ color: '#dc2626' }} />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 14, background: '#eff6ff' }}>
                            <Statistic title="Transactions" value={transactions.length} prefix={<LineChartOutlined />} valueStyle={{ color: '#2563eb' }} />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card
                    style={{ borderRadius: 14, marginBottom: 20 }}
                    title={<Space><FilterOutlined style={{ color: '#0d9488' }} /><Text strong>Filters</Text></Space>}
                >
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Bank Account</Text>
                            <Select
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="All Accounts"
                                value={filters.bank_account_id || undefined}
                                onChange={(value) => setFilters(prev => ({ ...prev, bank_account_id: value || '' }))}
                            >
                                {bankAccounts.map(account => (
                                    <Option key={account.id} value={String(account.id)}>{account.account_name} ({account.bank_name})</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Start Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD-MM-YYYY"
                                value={filters.start_date ? dayjs(filters.start_date) : null}
                                onChange={(date) => setFilters(prev => ({ ...prev, start_date: date ? date.format('YYYY-MM-DD') : '' }))}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>End Date</Text>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD-MM-YYYY"
                                value={filters.end_date ? dayjs(filters.end_date) : null}
                                onChange={(date) => setFilters(prev => ({ ...prev, end_date: date ? date.format('YYYY-MM-DD') : '' }))}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Type</Text>
                            <Select
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="All Types"
                                value={filters.transaction_type || undefined}
                                onChange={(value) => setFilters(prev => ({ ...prev, transaction_type: value || '' }))}
                            >
                                {transactionTypes.map(type => (
                                    <Option key={type} value={type}>{type}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Category</Text>
                            <Select
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="All Categories"
                                value={filters.transaction_category || undefined}
                                onChange={(value) => setFilters(prev => ({ ...prev, transaction_category: value || '' }))}
                            >
                                {transactionCategories.map(category => (
                                    <Option key={category} value={category}>{category.replace('_', ' ')}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={4}>
                            <Space>
                                <Button type="primary" onClick={applyFilters}>Apply</Button>
                                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Transactions Table */}
                <Card
                    style={{ borderRadius: 14 }}
                    title={<Space><LineChartOutlined style={{ color: '#0d9488' }} /><Text strong>Bank Transactions</Text></Space>}
                    extra={
                        <Space>
                            <Button danger icon={<FilePdfOutlined />} onClick={exportToPDF}>Export PDF</Button>
                            <Button icon={<DownloadOutlined />} onClick={exportToCSV} style={{ color: '#16a34a', borderColor: '#16a34a' }}>Export CSV</Button>
                        </Space>
                    }
                >
                    <Table
                        rowKey="id"
                        columns={transactionColumns}
                        dataSource={transactions}
                        loading={loading}
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        scroll={{ x: true }}
                        locale={{ emptyText: 'No transactions found for the selected criteria.' }}
                    />
                </Card>
            </div>
        </Layout>
    );
}
